import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button, ButtonGroup, Form, Spinner } from "react-bootstrap";
import Helmet from "react-helmet";
import moment from "moment";
import { fetchOrder, fetchMenuAllergens } from "state/prestationRoadSheet/actions";
import { MODELS, guessModel, defaultTitle, extractDishes, buildAllergenLegend, allergenNumbers, parseAllergenInput } from "./menuHelper";
import plate from "./assets/plate.png";
import brush from "./assets/brush.png";
import logo from "./assets/logo.png";
import brandonRegular from "./assets/brandon-regular.woff2";
import brandonBold from "./assets/brandon-bold.woff2";

// Texte modifiable directement sur la page (non contrôlé : React ne réécrit
// pas le contenu pendant la frappe, la valeur est relue à la sortie du champ).
const EditableField = ({ value, onChange, className, inline }) => {
  const Tag = inline ? "span" : "div";
  return (
    <Tag
      className={`pm-editable ${className || ""}`}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onChange(e.currentTarget.innerText.trim())}
    >
      {value}
    </Tag>
  );
};

// Remonté à chaque changement de valeur : le DOM édité à la main est remplacé
// proprement au lieu d'être réconcilié par React.
const Editable = (props) => <EditableField key={props.value} {...props} />;

// Plat + numéros d'allergènes en fin de ligne (petit, italique), renvoyant à la
// légende en bas de page ; les alertes (fiche manquante…) restent en toutes lettres.
// Un texte de plat vidé retire la ligne.
const DishList = ({ items, onChange, showAllergens, legend }) => {
  const update = (index, changes) =>
    onChange(items.map((it, i) => (i === index ? { ...it, ...changes } : it)).filter((it) => it.text));
  return (
  <>
    {items.map((item, index) => (
      <div className="pm-dish" key={`${index}-${item.text}`}>
        <Editable inline value={item.text} onChange={(text) => update(index, { text })} />
        {showAllergens && item.allergens && (item.warning || allergenNumbers(item.allergens, legend)) && (
          <>
            {" "}
            <Editable
              inline
              className={`pm-allergens${item.warning ? " pm-allergens-warning" : ""}`}
              value={item.warning ? item.allergens : allergenNumbers(item.allergens, legend)}
              onChange={(input) => update(index, { allergens: parseAllergenInput(input, legend), warning: false })}
            />
            {item.recipeUrl && item.warning && (
              <a className="pm-recipe-link no-print" href={item.recipeUrl} target="_blank" rel="noopener noreferrer" title="Rattacher la fiche recette dans le BO">
                ↗ rapprocher
              </a>
            )}
          </>
        )}
        <button
          type="button"
          className="pm-remove no-print"
          title="Retirer la ligne"
          onClick={() => onChange(items.filter((_, i) => i !== index))}
        >
          ×
        </button>
      </div>
    ))}
  </>
  );
};

// Menu A4 à imprimer pour une prestation, fidèle aux modèles Canva (buffet,
// cocktail, repas assis), pré-rempli avec les plats du devis et modifiable.
const PrintMenu = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const order = useSelector((state) => state.prestationRoadSheet.data.order);
  const isLoading = useSelector((state) => state.prestationRoadSheet.isLoading);

  const [model, setModel] = useState(null);
  const [title, setTitle] = useState({ light: "", bold: "" });
  const [salted, setSalted] = useState([]);
  const [sweet, setSweet] = useState([]);
  const [textScale, setTextScale] = useState(1);
  const [version, setVersion] = useState(0);
  const [allergenMap, setAllergenMap] = useState(null);
  const [showAllergens, setShowAllergens] = useState(true);
  const [fetchedOrderId, setFetchedOrderId] = useState(null);

  useEffect(() => {
    // Le store peut encore contenir la prestation précédente : « introuvable »
    // n'est affiché qu'une fois la requête terminée.
    Promise.resolve(dispatch(fetchOrder(orderId, "v2"))).finally(() => setFetchedOrderId(orderId));
    setAllergenMap(null);
    Promise.resolve(dispatch(fetchMenuAllergens(orderId)))
      .then((map) => setAllergenMap(map && typeof map === "object" && !map.error ? map : {}))
      .catch(() => setAllergenMap({}));
  }, [dispatch, orderId]);

  const isLoaded = order && parseInt(order.id) === parseInt(orderId);

  const reset = (nextModel) => {
    const m = nextModel || guessModel(order);
    const dishes = extractDishes(order, allergenMap);
    const count = dishes.salted.length + dishes.sweet.length;
    setModel(m);
    setTitle(defaultTitle(m, order));
    setSalted(dishes.salted);
    setSweet(dishes.sweet);
    setTextScale(count > 12 ? 0.8 : count > 9 ? 0.9 : 1);
    setVersion((v) => v + 1);
  };

  useEffect(() => {
    if (isLoaded && allergenMap) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, allergenMap]);

  const changeModel = (m) => {
    setModel(m);
    setTitle(defaultTitle(m, order));
    setVersion((v) => v + 1);
  };

  if (!isLoaded || !model) {
    return (
      <div className="text-center p-5">
        {fetchedOrderId === orderId && !isLoading && !isLoaded ? "Prestation introuvable" : <Spinner animation="border" />}
      </div>
    );
  }

  const config = MODELS[model];
  // Numérotation commune aux plats salés et sucrés (ordre réglementaire)
  const legend = buildAllergenLegend([...salted, ...sweet]);
  const docTitle = [
    order?.delivery?.delivery_date && moment(order.delivery.delivery_date).format("DD-MM-YY"),
    order?.company?.name,
    "Menu",
  ].filter(Boolean).join(" - ");

  return (
    <div className="pm-root" style={{ "--pm-scale": textScale }}>
      <Helmet>
        <title>{docTitle}</title>
      </Helmet>
      <style>{styles}</style>

      <div className="pm-toolbar no-print">
        <div>
          <b>{order?.numbering}</b> · {order?.company?.name} ·{" "}
          {order?.delivery?.delivery_date && moment(order.delivery.delivery_date).format("DD/MM/YYYY")} ·{" "}
          {order?.format || order?.prestation_type_fr}
        </div>
        <ButtonGroup size="sm">
          {Object.entries(MODELS).map(([key, m]) => (
            <Button key={key} variant={key === model ? "success" : "outline-success"} onClick={() => changeModel(key)}>
              {m.label}
            </Button>
          ))}
        </ButtonGroup>
        <Form.Check
          type="switch"
          id="pm-show-allergens"
          label="Allergènes"
          checked={showAllergens}
          onChange={(e) => setShowAllergens(e.target.checked)}
        />
        <Form.Group className="pm-scale mb-0">
          <Form.Label className="mb-0 mr-2">Taille du texte</Form.Label>
          <Form.Control
            type="range"
            min={0.6}
            max={1.2}
            step={0.05}
            value={textScale}
            onChange={(e) => setTextScale(parseFloat(e.target.value))}
          />
        </Form.Group>
        <div>
          <Button size="sm" variant="outline-secondary" className="mr-2" onClick={() => setSalted([...salted, { text: "Nouveau plat", allergens: "" }])}>
            + Plat
          </Button>
          <Button size="sm" variant="outline-secondary" className="mr-2" onClick={() => setSweet([...sweet, { text: "Nouveau dessert", allergens: "" }])}>
            + Dessert
          </Button>
          <Button size="sm" variant="outline-danger" className="mr-2" onClick={() => reset(model)}>
            Réinitialiser
          </Button>
          <Button size="sm" variant="primary" onClick={() => window.print()}>
            Imprimer
          </Button>
        </div>
        <small className="text-muted w-100">
          Cliquez sur un texte pour le modifier. Les modifications ne sont pas enregistrées : imprimez (ou « Enregistrer en PDF ») une fois le menu prêt.
        </small>
      </div>

      <div className="pm-page" key={version}>
        <svg className="pm-arch" viewBox="0 0 1414 2000" preserveAspectRatio="none">
          <path d="M150,1940 L150,616 A556,556 0 0 1 1262,616 L1262,1940 Z" />
        </svg>
        <div className="pm-content">
          <div className="pm-title">
            <Editable className="pm-title-light" value={title.light} onChange={(light) => setTitle({ ...title, light })} />
            <Editable className="pm-title-bold" value={title.bold} onChange={(bold) => setTitle({ ...title, bold })} />
          </div>
          {config.ornament === "plate" ? (
            <img className="pm-plate" src={plate} alt="" />
          ) : (
            <img className="pm-brush" src={brush} alt="" />
          )}
          <div className="pm-dishes">
            <DishList items={salted} onChange={setSalted} showAllergens={showAllergens} legend={legend} />
            {config.separator && salted.length > 0 && sweet.length > 0 && <div className="pm-separator" />}
            <DishList items={sweet} onChange={setSweet} showAllergens={showAllergens} legend={legend} />
          </div>
          {showAllergens && legend.length > 0 && (
            <div className="pm-allergen-legend">
              Allergènes : {legend.map((a, i) => `${a} (${i + 1})`).join(" · ")}
            </div>
          )}
          <img className="pm-logo" src={logo} alt="Le Moulin Traiteur" />
        </div>
      </div>
    </div>
  );
};

// Mesures relevées sur les exports Canva (1414 × 2000 px) ramenées à l'A4.
const styles = `
  @font-face { font-family: 'PmBrandon'; src: url(${brandonRegular}) format('woff2'); font-weight: 400; }
  @font-face { font-family: 'PmBrandon'; src: url(${brandonBold}) format('woff2'); font-weight: 700; }
  @page { size: A4; margin: 0; }
  body { background: #eef1ef; }
  .pm-root { font-family: 'PmBrandon', sans-serif; padding-bottom: 40px; }
  .pm-toolbar { display: flex; flex-wrap: wrap; gap: 12px 24px; align-items: center; justify-content: center;
    padding: 12px 16px; background: #fff; border-bottom: 1px solid #dde3df; font-family: sans-serif; font-size: 14px;
    position: sticky; top: 0; z-index: 10; }
  .pm-toolbar small { text-align: center; }
  .pm-scale { display: flex; align-items: center; }
  .pm-scale input { width: 140px; }
  .pm-page { position: relative; width: 210mm; height: 297mm; margin: 24px auto; background: #fff;
    box-shadow: 0 2px 12px rgba(0,0,0,.12); overflow: hidden; color: #729086; }
  .pm-arch { position: absolute; inset: 0; width: 100%; height: 100%; }
  .pm-arch path { fill: none; stroke: #689d71; stroke-width: 3; stroke-dasharray: 2 5; }
  .pm-content { position: absolute; left: 16%; right: 16%; top: 11%; bottom: 3.5%;
    display: flex; flex-direction: column; align-items: center; text-align: center; }
  .pm-title { text-transform: uppercase; line-height: 1.12; font-size: 13.8mm; letter-spacing: .01em; }
  .pm-title-light { font-weight: 400; white-space: pre-line; }
  .pm-title-bold { font-weight: 700; }
  .pm-plate { width: 23mm; margin: 6mm 0 5mm; }
  .pm-brush { width: 44mm; margin: 5mm 0 6mm; }
  .pm-dishes { flex: 1; width: 100%; display: flex; flex-direction: column; justify-content: center;
    font-size: calc(6.1mm * var(--pm-scale)); line-height: 1.3; }
  .pm-dish { position: relative; margin: 0 0 calc(4.2mm * var(--pm-scale)); }
  .pm-dish:last-child { margin-bottom: 0; }
  .pm-separator { width: 30%; margin: calc(3mm * var(--pm-scale)) auto calc(7mm * var(--pm-scale));
    border-top: 1px dotted #729086; }
  .pm-logo { width: 17mm; margin-top: 6mm; }
  .pm-editable { outline: none; border-radius: 3px; cursor: text; }
  .pm-editable:hover { background: rgba(104,157,113,.08); }
  .pm-editable:focus { background: rgba(104,157,113,.15); }
  .pm-allergens { font-style: italic; font-size: .62em; }
  .pm-allergens::before { content: "("; }
  .pm-allergens::after { content: ")"; }
  .pm-allergens-warning { color: #c0392b; }
  .pm-allergen-legend { width: 100%; margin-top: 5mm; font-size: 3.2mm; font-style: italic; line-height: 1.4; }
  .pm-recipe-link { margin-left: 2mm; font-family: sans-serif; font-size: 11px; color: #c0392b; text-decoration: underline; }
  .pm-remove { position: absolute; right: -9mm; top: 0; border: 0; background: none; color: #c0392b;
    font-size: 20px; line-height: 1; opacity: 0; cursor: pointer; }
  .pm-dish:hover .pm-remove { opacity: 1; }
  @media screen and (max-width: 840px) {
    .pm-page { zoom: .58; }
  }
  @media print {
    /* Bootstrap impose body { min-width: 992px } à l'impression : plus large que l'A4
       (794px), la page était réduite à ~80 % et calée à gauche. */
    body { background: #fff; min-width: 0 !important; margin: 0; }
    .no-print { display: none !important; }
    .pm-root { padding: 0; }
    .pm-page { margin: 0; box-shadow: none; }
    .pm-editable:hover, .pm-editable:focus { background: none; }
  }
`;

export default PrintMenu;
